// src/services/apiService.js
class ApiService {
  constructor() {
    // Relative by default: the Spring application serves this bundle, so /api
    // is the same origin on any host. An absolute URL here has to be changed
    // per environment and silently breaks under HTTPS.
    this.baseURL = process.env.REACT_APP_API_URL || '/api';
    this.token = localStorage.getItem('accessToken');
    console.log('ApiService initialized with:', {
      baseURL: this.baseURL,
      hasToken: !!this.token,
      tokenPreview: this.token ? this.token.substring(0, 20) + '...' : 'No token'
    });
  }

  // Set authorization header
  getHeaders(includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth && this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
      console.log('Adding auth header with token:', this.token.substring(0, 20) + '...');
    } else {
      console.log('No token available for auth header. Token exists:', !!this.token);
    }

    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(options.includeAuth !== false),
        ...options.headers,
      },
    };

    // Debug logging
    console.log('API Request:', {
      url,
      method: config.method || 'GET',
      hasAuth: !!this.token,
      headers: config.headers
    });

    try {
      const response = await fetch(url, config);

      if (response.status === 401) {
        console.log('401 Unauthorized - attempting token refresh');
        // Token expired, try refresh
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry with new token
          config.headers['Authorization'] = `Bearer ${this.token}`;
          const retryResponse = await fetch(url, config);
          return this.handleResponse(retryResponse);
        } else {
          // Refresh failed, clear auth and throw error
          this.clearAuth();
          throw new Error('Session expired. Please login again.');
        }
      }

      return this.handleResponse(response);
    } catch (error) {
      console.error('API Request failed:', error);
      throw error;
    }
  }

  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`;
      throw new Error(errorMessage);
    }

    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }
    
    return response.text();
  }

  // Authentication methods
  async login(credentials) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
      includeAuth: false,
    });

    // Debug: Log the actual response from backend
    console.log('Backend login response:', response);

    // Your backend returns "token" not "accessToken"
    if (response.token) {
      this.token = response.token;
      localStorage.setItem('accessToken', response.token);  // Store as accessToken for consistency
      localStorage.setItem('refreshToken', response.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      console.log('Token saved successfully');
    } else {
      console.error('No token found in response. Response keys:', Object.keys(response));
    }

    return response;
  }

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) return false;

      const response = await this.request(`/auth/refresh?refreshToken=${refreshToken}`, {
        method: 'POST',
        includeAuth: false,
      });

      if (response.accessToken) {
        this.token = response.accessToken;
        localStorage.setItem('accessToken', response.accessToken);
        localStorage.setItem('refreshToken', response.refreshToken);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  async logout() {
    try {
      if (this.token) {
        await this.request('/auth/logout', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${this.token}` }
        });
      }
    } catch (error) {
      console.error('Logout request failed:', error);
    } finally {
      this.clearAuth();
    }
  }

  clearAuth() {
    this.token = null;
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  }

  // ===============
  // COMPANY APIs
  // ===============

  async getAllCompanies() {
    return this.request('/api/companies');
  }

  async getCompanyById(id) {
    return this.request(`/api/companies/${id}`);
  }

  // ===============
  // DASHBOARD APIs
  // ===============

  async getDashboardData() {
    return this.request('/dashboard');
  }

  async getDashboardDataByCompany(companyId) {
    return this.request(`/dashboard/company/${companyId}`);
  }

  // ===============
  // PRODUCT APIs
  // ===============

  async getAllProducts() {
    return this.request('/products');
  }

  async getProductsByCompany(companyId) {
    return this.request(`/products/company/${companyId}`);
  }

  async getActiveProducts() {
    return this.request('/products/active');
  }

  async getActiveProductsByCompany(companyId) {
    return this.request(`/products/company/${companyId}/active`);
  }

  async getProductById(id) {
    return this.request(`/products/${id}`);
  }

  async getProductBySku(sku) {
    return this.request(`/products/sku/${encodeURIComponent(sku)}`);
  }

  async getProductsByCategory(category) {
    return this.request(`/products/category/${encodeURIComponent(category)}`);
  }

  async getAllCategories() {
    return this.request('/products/categories');
  }

  async getCategoriesByCompany(companyId) {
    return this.request(`/products/company/${companyId}/categories`);
  }

  async searchProducts(query) {
    return this.request(`/products/search?query=${encodeURIComponent(query)}`);
  }

  async searchProductsByCompany(companyId, query) {
    return this.request(`/products/company/${companyId}/search?query=${encodeURIComponent(query)}`);
  }

  async getProductsByPriceRange(minPrice, maxPrice) {
    return this.request(`/products/price-range?minPrice=${minPrice}&maxPrice=${maxPrice}`);
  }

  async getLowStockProducts(threshold = 10) {
    return this.request(`/products/low-stock?threshold=${threshold}`);
  }

  async getLowStockProductsByCompany(companyId, threshold = 10) {
    return this.request(`/products/company/${companyId}/low-stock?threshold=${threshold}`);
  }

  async createProduct(productData) {
    return this.request('/products', {
      method: 'POST',
      body: JSON.stringify(productData),
    });
  }

  async updateProduct(id, productData) {
    return this.request(`/products/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    });
  }

  async updateStock(id, quantity) {
    return this.request(`/products/${id}/stock?quantity=${quantity}`, {
      method: 'PUT',
    });
  }

  async deleteProduct(id) {
    return this.request(`/products/${id}`, {
      method: 'DELETE',
    });
  }

   // ===============
   // EXPENSES APIs
  // ===============

  async getAllExpenses() {
    return this.request('/expenses');
  }

  async getExpensesByCompany(companyId) {
    return this.request(`/expenses/company/${companyId}`);
  }

  async getAllMonthlyExpenses() {
    return this.request('/expenses/monthlyexpenses');
  }

  async getMonthlyExpensesByCompany(companyId) {
    return this.request(`/expenses/company/${companyId}/monthlyexpenses`);
  }

  async getExpenseById(id) {
    return this.request(`/expenses/${id}`);
  }

  async searchExpenses(query) {
    return this.request(`/expenses/search?query=${encodeURIComponent(query)}`);
  }

  async searchExpensesByCompany(companyId, query) {
    return this.request(`/expenses/company/${companyId}/search?query=${encodeURIComponent(query)}`);
  }

  async getExpensePayments(expenseId){
    return this.request(`/expenses/${expenseId}/payments`);
  }

  async createExpensePayment(paymentData) {
    return this.request('/expenses/payments',{
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }
  
  async createExpense(expenseData) {
    return this.request('/expenses', {
      method: 'POST',
      body: JSON.stringify(expenseData),
    });
  }

  async updateExpense(id, expenseData) {
    return this.request(`/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(expenseData),
    });
  }
  
  async deleteExpense(id) {
    return this.request(`/expenses/${id}`, {
      method: 'DELETE',
    });
  }


  // ===============
  // CUSTOMER APIs
  // ===============

  async getAllCustomers() {
    return this.request('/customers');
  }

  async getActiveCustomers() {
    return this.request('/customers/active');
  }

  async getCustomerById(id) {
    return this.request(`/customers/${id}`);
  }

  async getCustomersByType(type) {
    return this.request(`/customers/type/${type}`);
  }

  async getCustomersByCity(city) {
    return this.request(`/customers/city/${encodeURIComponent(city)}`);
  }

  async searchCustomers(query) {
    return this.request(`/customers/search?query=${encodeURIComponent(query)}`);
  }

  async getTopCustomers() {
    return this.request('/customers/top');
  }

  async createCustomer(customerData) {
    return this.request('/customers', {
      method: 'POST',
      body: JSON.stringify(customerData),
    });
  }

  async updateCustomer(id, customerData) {
    return this.request(`/customers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async deleteCustomer(id) {
    return this.request(`/customers/${id}`, {
      method: 'DELETE',
    });
  }

  // ===============
  // INVOICE APIs
  // ===============

  async getAllInvoices() {
    return this.request('/invoices');
  }

  async getInvoicesByCompany(companyId) {
    return this.request(`/invoices/company/${companyId}`);
  }

  async getInvoiceById(id) {
    return this.request(`/invoices/${id}`);
  }

  async getInvoiceByNumber(invoiceNumber) {
    return this.request(`/invoices/number/${encodeURIComponent(invoiceNumber)}`);
  }

  async getInvoicesByCustomer(customerId) {
    return this.request(`/invoices/customer/${customerId}`);
  }

  async getInvoicesByStatus(status) {
    return this.request(`/invoices/status/${status}`);
  }

  async getInvoicesByCompanyAndStatus(companyId, status) {
    return this.request(`/invoices/company/${companyId}/status/${status}`);
  }

  async getInvoicesByDateRange(startDate, endDate) {
    return this.request(`/invoices/date-range?startDate=${startDate}&endDate=${endDate}`);
  }

  async getOverdueInvoices() {
    return this.request('/invoices/overdue');
  }

  async getOverdueInvoicesByCompany(companyId) {
    return this.request(`/invoices/company/${companyId}/overdue`);
  }

  async getOutstandingInvoicesByCustomer(customerId) {
    return this.request(`/invoices/outstanding/customer/${customerId}`);
  }

  async getRecentInvoices(limit = 10) {
    return this.request(`/invoices/recent?limit=${limit}`);
  }

  async getRecentInvoicesByCompany(companyId, limit = 10) {
    return this.request(`/invoices/company/${companyId}/recent?limit=${limit}`);
  }

  async searchInvoices(query) {
    return this.request(`/invoices/search?query=${encodeURIComponent(query)}`);
  }

  async searchInvoicesByCompany(companyId, query) {
    return this.request(`/invoices/company/${companyId}/search?query=${encodeURIComponent(query)}`);
  }

  async createInvoice(invoiceData) {
    return this.request('/invoices', {
      method: 'POST',
      body: JSON.stringify(invoiceData),
    });
  }

  async updateInvoice(id, invoiceData) {
    return this.request(`/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(invoiceData),
    });
  }

  async reassignInvoiceCustomer(id, customerData) {
    return this.request(`/invoices/${id}/customer`, {
      method: 'PUT',
      body: JSON.stringify(customerData),
    });
  }

  async updateInvoiceStatus(id, status) {
    return this.request(`/invoices/${id}/status?status=${status}`, {
      method: 'PUT',
    });
  }

  async deleteInvoice(id) {
    return this.request(`/invoices/${id}`, {
      method: 'DELETE',
    });
  }

  // ===============
  // PAYMENT APIs
  // ===============

  async getAllPayments() {
    return this.request('/payments');
  }

  async getPaymentById(id) {
    return this.request(`/payments/${id}`);
  }

  async getPaymentsByInvoice(invoiceId) {
    return this.request(`/payments/invoice/${invoiceId}`);
  }

  async getPaymentsByCustomer(customerId) {
    return this.request(`/payments/customer/${customerId}`);
  }

  async getPaymentsByMethod(method) {
    return this.request(`/payments/method/${method}`);
  }

  async getPaymentsByDateRange(startDate, endDate) {
    return this.request(`/payments/date-range?startDate=${startDate}&endDate=${endDate}`);
  }

  async getRecentPayments(limit = 10) {
    return this.request(`/payments/recent?limit=${limit}`);
  }

  async searchPayments(query) {
    return this.request(`/payments/search?query=${encodeURIComponent(query)}`);
  }

  async createPayment(paymentData) {
    return this.request('/payments', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
  }

  async updatePayment(id, paymentData) {
    return this.request(`/payments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(paymentData),
    });
  }

  async updatePaymentStatus(id, status) {
    return this.request(`/payments/${id}/status?status=${status}`, {
      method: 'PUT',
    });
  }

  async deletePayment(id) {
    return this.request(`/payments/${id}`, {
      method: 'DELETE',
    });
  }

  // ===============
 // STOCK MOVEMENT APIs
 // ===============

async replenishProductStock(productId, replenishmentData) {
  return this.request(`/products/${productId}/replenish`, {
    method: 'POST',
    body: JSON.stringify(replenishmentData),
  });
}

 // Transfer stock from store/warehouse to shop
  async transferToShop(productId, data) {
    return this.request(`/products/${productId}/transfer-to-shop`, {
      method: 'POST',
      //headers: this.getHeaders(),
      body: JSON.stringify(data)
    });
   
  }

async getProductStockMovements(productId) {
  return this.request(`/products/${productId}/stock-movements`);
}

async createStockMovement(movementData) {
  return this.request('/stock-movements', {
    method: 'POST',
    body: JSON.stringify(movementData),
  });
}

async getStockMovementsByCompany(companyId) {
  return this.request(`/stock-movements/company/${companyId}`);
}

async getRecentStockMovementsByCompany(companyId, limit = 20) {
  return this.request(`/stock-movements/company/${companyId}/recent?limit=${limit}`);
}

async getStockMovementsByType(type) {
  return this.request(`/stock-movements/type/${type}`);
}

async getStockMovementsByDateRange(startDate, endDate) {
  return this.request(`/stock-movements/date-range?startDate=${startDate}&endDate=${endDate}`);
}

async getRecentStockMovements(limit = 20) {
  return this.request(`/stock-movements/recent?limit=${limit}`);
}

async checkStockAvailability(productId, quantity) {
  return this.request(`/stock-movements/check-stock/${productId}?quantity=${quantity}`);
}

// Update existing product methods if needed
async updateProductStock(productId, quantity) {
  return this.request(`/products/${productId}/stock?quantity=${quantity}`, {
    method: 'PUT',
  });
}

// ===============
  // ROLE APIs
  // ===============

  async getAllRoles() {
    return this.request('/roles');
  }

  async getActiveRoles() {
    return this.request('/roles/active');
  }

  async getRoleById(id) {
    return this.request(`/roles/${id}`);
  }

  async getRoleByName(name) {
    return this.request(`/roles/name/${encodeURIComponent(name)}`);
  }

  async createRole(roleData) {
    return this.request('/roles', {
      method: 'POST',
      body: JSON.stringify(roleData),
    });
  }

  async updateRole(id, roleData) {
    return this.request(`/roles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(roleData),
    });
  }

  async deleteRole(id) {
    return this.request(`/roles/${id}`, {
      method: 'DELETE',
    });
  }

  async assignPermissionsToRole(roleId, permissionIds) {
    return this.request(`/roles/${roleId}/permissions`, {
      method: 'POST',
      body: JSON.stringify(permissionIds),
    });
  }

  async removePermissionFromRole(roleId, permissionId) {
    return this.request(`/roles/${roleId}/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  async getRolePermissions(roleId) {
    return this.request(`/roles/${roleId}/permissions`);
  }

  async getUsersCountForRole(roleId) {
    return this.request(`/roles/${roleId}/users-count`);
  }

  // ===============
  // PERMISSION APIs
  // ===============

  async getAllPermissions() {
    return this.request('/permissions');
  }

  async getPermissionById(id) {
    return this.request(`/permissions/${id}`);
  }

  async getDistinctModules() {
    return this.request('/permissions/modules');
  }

  async getPermissionsByModule(module) {
    return this.request(`/permissions/module/${encodeURIComponent(module)}`);
  }

  async createPermission(permissionData) {
    return this.request('/permissions', {
      method: 'POST',
      body: JSON.stringify(permissionData),
    });
  }

  async updatePermission(id, permissionData) {
    return this.request(`/permissions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(permissionData),
    });
  }

  async deletePermission(id) {
    return this.request(`/permissions/${id}`, {
      method: 'DELETE',
    });
  }

  async assignPermissionToRole(roleId, permissionId) {
    return this.request(`/permissions/roles/${roleId}/permissions/${permissionId}`, {
      method: 'POST',
    });
  }

  async removePermissionFromRoleByPermission(roleId, permissionId) {
    return this.request(`/permissions/roles/${roleId}/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  async assignDirectPermissionToUser(userId, permissionId) {
    return this.request(`/permissions/users/${userId}/permissions/${permissionId}`, {
      method: 'POST',
    });
  }

  async removeDirectPermissionFromUser(userId, permissionId) {
    return this.request(`/permissions/users/${userId}/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  async getUserDirectPermissions(userId) {
    return this.request(`/permissions/users/${userId}/direct`);
  }

  async getUserAllPermissions(userId) {
    return this.request(`/permissions/users/${userId}/all`);
  }

  async checkPermission(permissionName) {
    return this.request(`/permissions/check/${encodeURIComponent(permissionName)}`);
  }

  async checkAnyPermission(permissionNames) {
    return this.request('/permissions/check/any', {
      method: 'POST',
      body: JSON.stringify(permissionNames),
    });
  }

  async checkAllPermissions(permissionNames) {
    return this.request('/permissions/check/all', {
      method: 'POST',
      body: JSON.stringify(permissionNames),
    });
  }

  async initializeDefaultPermissions() {
    return this.request('/permissions/initialize', {
      method: 'POST',
    });
  }

  // ===============
  // MENU APIs
  // ===============

  async getAllMenus() {
    return this.request('/menus');
  }

  async getMenuById(id) {
    return this.request(`/menus/${id}`);
  }

  async getMenuTree() {
    return this.request('/menus/tree');
  }

  async getUserMenus() {
    return this.request('/menus/user-menus');
  }

  async getUserMenusByUsername(username) {
    return this.request(`/menus/user-menus/${encodeURIComponent(username)}`);
  }

  async getRootMenus() {
    return this.request('/menus/root');
  }

  async getChildMenus(parentId) {
    return this.request(`/menus/${parentId}/children`);
  }

  async createMenu(menuData) {
    return this.request('/menus', {
      method: 'POST',
      body: JSON.stringify(menuData),
    });
  }

  async updateMenu(id, menuData) {
    return this.request(`/menus/${id}`, {
      method: 'PUT',
      body: JSON.stringify(menuData),
    });
  }

  async deleteMenu(id) {
    return this.request(`/menus/${id}`, {
      method: 'DELETE',
    });
  }

  async assignPermissionToMenu(menuId, permissionId) {
    return this.request(`/menus/${menuId}/permissions/${permissionId}`, {
      method: 'POST',
    });
  }

  async removePermissionFromMenu(menuId, permissionId) {
    return this.request(`/menus/${menuId}/permissions/${permissionId}`, {
      method: 'DELETE',
    });
  }

  async reorderMenus(menuIds) {
    return this.request('/menus/reorder', {
      method: 'PUT',
      body: JSON.stringify(menuIds),
    });
  }

  async clearMenuCache() {
    return this.request('/menus/clear-cache', {
      method: 'POST',
    });
  }

  // ===============
  // ENHANCED USER APIs (extending existing ones)
  // ===============

  async getAllUsers() {
    return this.request('/users');
  }

  async getUserById(id) {
    return this.request(`/users/${id}`);
  }

  async getUserByUsername(username) {
    return this.request(`/users/username/${encodeURIComponent(username)}`);
  }

  async getUsersByRole(role) {
    return this.request(`/users/role/${encodeURIComponent(role)}`);
  }

  async searchUsers(query) {
    return this.request(`/users/search?query=${encodeURIComponent(query)}`);
  }

  async createUser(userData) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async updateUser(id, userData) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  // ===============
  // USER PROFILE APIs
  // ===============

  async getCurrentUserProfile() {
    return this.request('/profile');
  }

  async updateProfile(profileData) {
    return this.request('/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async changePassword(passwordData) {
    return this.request('/profile/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  }

  async updateProfilePicture(profilePictureUrl) {
    return this.request(`/profile/profile-picture?profilePictureUrl=${encodeURIComponent(profilePictureUrl)}`, {
      method: 'POST',
    });
  }

  async enableTwoFactorAuth() {
    return this.request('/profile/enable-2fa', {
      method: 'POST',
    });
  }

  async disableTwoFactorAuth() {
    return this.request('/profile/disable-2fa', {
      method: 'POST',
    });
  }

  async updateNotificationPreferences(preferences) {
    return this.request('/profile/notifications', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    });
  }

  // ===============
  // REPORT APIs
  // ===============

  // Sales Reports
  async generateSalesReport(reportParams) {
    return this.request('/reports/sales', {
      method: 'POST',
      body: JSON.stringify(reportParams),
    });
  }

  async getDailySalesReport(date) {
    return this.request(`/reports/sales/daily?date=${date}`);
  }

  async getMonthlySalesReport(year, month) {
    return this.request(`/reports/sales/monthly?year=${year}&month=${month}`);
  }

  async getYearlySalesReport(year) {
    return this.request(`/reports/sales/yearly?year=${year}`);
  }

  // Customer Reports
  async generateCustomerReport(reportParams) {
    return this.request('/reports/customers', {
      method: 'POST',
      body: JSON.stringify(reportParams),
    });
  }

  async getCustomerAnalysisReport(startDate, endDate) {
    return this.request(`/reports/customers/analysis?startDate=${startDate}&endDate=${endDate}`);
  }

  async getCustomerRetentionReport(months) {
    return this.request(`/reports/customers/retention?months=${months}`);
  }

  // Invoice Reports
  async generateInvoiceReport(reportParams) {
    return this.request('/reports/invoices', {
      method: 'POST',
      body: JSON.stringify(reportParams),
    });
  }

  async getInvoiceAgingReport() {
    return this.request('/reports/invoices/aging');
  }

  async getOverdueInvoicesReport() {
    return this.request('/reports/invoices/overdue');
  }

  // Payment Reports
  async generatePaymentReport(reportParams) {
    return this.request('/reports/payments', {
      method: 'POST',
      body: JSON.stringify(reportParams),
    });
  }

  async getCashFlowReport(startDate, endDate) {
    return this.request(`/reports/payments/cashflow?startDate=${startDate}&endDate=${endDate}`);
  }

  async getPaymentMethodAnalysis(startDate, endDate) {
    return this.request(`/reports/payments/methods?startDate=${startDate}&endDate=${endDate}`);
  }

  // Inventory Reports
  async generateInventoryReport(reportParams) {
    return this.request('/reports/inventory', {
      method: 'POST',
      body: JSON.stringify(reportParams),
    });
  }

  async getStockLevelsReport() {
    return this.request('/reports/inventory/stock-levels');
  }

  async getLowStockAlert() {
    return this.request('/reports/inventory/low-stock');
  }

  // Export Reports
  async exportReportToPdf(exportRequest) {
    const response = await this.request('/reports/export/pdf', {
      method: 'POST',
      body: JSON.stringify(exportRequest),
    });
    return response; // This should return a blob for download
  }

  async exportReportToExcel(exportRequest) {
    const response = await this.request('/reports/export/excel', {
      method: 'POST',
      body: JSON.stringify(exportRequest),
    });
    return response; // This should return a blob for download
  }

  async exportReportToCsv(exportRequest) {
    const response = await this.request('/reports/export/csv', {
      method: 'POST',
      body: JSON.stringify(exportRequest),
    });
    return response; // This should return a blob for download
  }

  async emailReport(exportRequest) {
    return this.request('/reports/email', {
      method: 'POST',
      body: JSON.stringify(exportRequest),
    });
  }

   
  // ===============
  // RETURN & EXCHANGE APIs
  // ===============
  // ADD these methods to your existing apiService.js class (before the closing brace)

  async processFullReturn(returnData) {
    return this.request('/returns/full-return', {
      method: 'POST',
      body: JSON.stringify(returnData),
    });
  }

  async processExchange(exchangeData) {
    return this.request('/returns/exchange', {
      method: 'POST',
      body: JSON.stringify(exchangeData),
    });
  }

  async getReturnById(id) {
    return this.request(`/returns/${id}`);
  }

  async getReturnByNumber(returnNumber) {
    return this.request(`/returns/number/${encodeURIComponent(returnNumber)}`);
  }

  async getReturnsByCompany(companyId) {
    return this.request(`/returns/company/${companyId}`);
  }

  async getReturnsByInvoice(invoiceId) {
    return this.request(`/returns/invoice/${invoiceId}`);
  }

  async getReturnsByCompanyAndType(companyId, returnType) {
    return this.request(`/returns/company/${companyId}/type/${returnType}`);
  }

  async getReturnsByCompanyAndDateRange(companyId, startDate, endDate) {
    return this.request(`/returns/company/${companyId}/date-range?startDate=${startDate}&endDate=${endDate}`);
  }

  async getRecentReturnsByCompany(companyId, limit = 20) {
    return this.request(`/returns/company/${companyId}/recent?limit=${limit}`);
  }

  async searchReturnsByCompany(companyId, query) {
    return this.request(`/returns/company/${companyId}/search?query=${encodeURIComponent(query)}`);
  }

  async checkReturnEligibility(invoiceId) {
    return this.request(`/returns/invoice/${invoiceId}/eligible`);
  }

  // Utility methods
  isAuthenticated() {
    return !!this.token;
  }

  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  // Refresh token from localStorage (useful for debugging)
  refreshTokenFromStorage() {
    this.token = localStorage.getItem('accessToken');
    console.log('Token refreshed from localStorage:', !!this.token);
    return !!this.token;
  }

  // Update the token manually
  updateToken(newToken) {
    this.token = newToken;
    console.log('Token updated manually:', !!newToken);
  }
}

// Create and export singleton instance
const apiService = new ApiService();
export default apiService;